/* =====================================================
   VYNA PET — Main JavaScript
   ===================================================== */

'use strict';

/* --- Utilidades --- */
const VYNA = {
  // Fetch wrapper con reintentos
  async fetchJSON(url, options = {}) {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      ...options
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  },

  // Formatear precio (centavos → euros)
  formatMoney(cents) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(cents / 100);
  },

  // Disparar evento personalizado
  emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(`vyna:${name}`, { detail, bubbles: true }));
  },

  // Cookie helpers
  setCookie(name, value, days = 30) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/;SameSite=Lax`;
  },
  getCookie(name) {
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
      const [key, val] = c.trim().split('=');
      if (key === name) return val;
    }
    return null;
  }
};

/* =========================================
   CARRITO
   ========================================= */
class VynaCart {
  constructor() {
    this.drawer   = document.getElementById('cart-drawer');
    this.overlay  = document.getElementById('cart-overlay');
    this.toggle   = document.getElementById('cart-toggle');
    this.closeBtn = document.getElementById('cart-close');
    this.itemsEl  = document.getElementById('cart-items');
    this.isOpen   = false;

    if (!this.drawer) return;
    this.bindEvents();
  }

  bindEvents() {
    this.toggle?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Add to cart buttons (product cards)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.product-card__atc');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      const variantId = parseInt(btn.dataset.variantId);
      if (variantId) this.addItem(variantId, 1);
    });

    // PDP Add to Cart
    const pdpATC = document.getElementById('add-to-cart-btn');
    pdpATC?.addEventListener('click', () => {
      const variantId    = parseInt(document.getElementById('selected-variant-id')?.value);
      const sellingPlanId = parseInt(document.getElementById('selected-selling-plan')?.value) || null;
      if (variantId) this.addItem(variantId, 1, sellingPlanId);
    });

    // PDP Buy Now
    const buyNow = document.getElementById('buy-now-btn');
    buyNow?.addEventListener('click', async () => {
      const variantId    = parseInt(document.getElementById('selected-variant-id')?.value);
      const sellingPlanId = parseInt(document.getElementById('selected-selling-plan')?.value) || null;
      if (!variantId) return;
      if (sellingPlanId) {
        await this.addItem(variantId, 1, sellingPlanId);
        window.location.href = '/checkout';
      } else {
        window.location.href = `/cart/${variantId}:1?checkout=true`;
      }
    });

    // Qty controls en carrito
    this.drawer?.addEventListener('click', (e) => {
      const key = e.target.closest('[data-key]')?.dataset.key;
      if (!key) return;

      if (e.target.closest('.cart-item__qty-increase')) {
        this.updateQty(key, 1);
      } else if (e.target.closest('.cart-item__qty-decrease')) {
        this.updateQty(key, -1);
      } else if (e.target.closest('.cart-item__remove')) {
        this.removeItem(key);
      }
    });

    // Evento global post-add
    document.addEventListener('vyna:item_added', () => this.refresh());
  }

  open() {
    this.isOpen = true;
    this.drawer.hidden = false;
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.toggle?.setAttribute('aria-expanded', 'true');
    setTimeout(() => this.drawer.classList.add('is-open'), 10);
    this.refresh();
    // Focus trap
    this.closeBtn?.focus();
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.toggle?.setAttribute('aria-expanded', 'false');
    setTimeout(() => { this.drawer.hidden = true; }, 400);
    this.toggle?.focus();
  }

  async addItem(variantId, quantity = 1, sellingPlanId = null) {
    try {
      // Optimistic UI
      const btn = document.querySelector(`[data-variant-id="${variantId}"]`);
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Añadiendo...';
      }

      const cartBody = { id: variantId, quantity };
      if (sellingPlanId) cartBody.selling_plan = sellingPlanId;
      await VYNA.fetchJSON('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify(cartBody)
      });

      // Analytics
      VYNA.emit('add_to_cart', { variant_id: variantId, quantity });
      this.trackAddToCart(variantId, quantity);

      // Restaurar botón
      if (btn) {
        btn.textContent = '✓ Añadido';
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = 'Añadir al carrito';
        }, 2000);
      }

      VYNA.emit('item_added');
      this.open();
    } catch (err) {
      console.error('Error añadiendo al carrito:', err);
    }
  }

  async updateQty(key, delta) {
    try {
      const cart = await VYNA.fetchJSON('/cart.js');
      const item = cart.items.find(i => i.key === key);
      if (!item) return;
      const newQty = Math.max(0, item.quantity + delta);
      await VYNA.fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      this.refresh();
    } catch (err) {
      console.error('Error actualizando cantidad:', err);
    }
  }

  async removeItem(key) {
    try {
      await VYNA.fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      this.refresh();
    } catch (err) {
      console.error('Error eliminando producto:', err);
    }
  }

  async refresh() {
    try {
      const cart = await VYNA.fetchJSON('/cart.js');
      this.renderCart(cart);
      this.updateCount(cart.item_count);
      this.updateTotal(cart.total_price);
    } catch (err) {
      console.error('Error actualizando carrito:', err);
    }
  }

  renderCart(cart) {
    if (!this.itemsEl) return;
    if (cart.item_count === 0) {
      this.itemsEl.innerHTML = `
        <div class="cart-empty" style="text-align:center;padding:var(--space-12) var(--space-6);">
          <div style="font-size:3rem;margin-bottom:var(--space-4);">🐶</div>
          <p style="font-weight:600;color:var(--color-primary);margin-bottom:var(--space-2);">Tu carrito está vacío</p>
          <p style="font-size:var(--text-sm);color:var(--color-text-muted);margin-bottom:var(--space-6);">Descubre nuestras fórmulas premium para tu perro</p>
          <a href="/collections/all" class="btn btn--primary btn--pill">Ver productos</a>
        </div>`;
      return;
    }

    const freeShipping = 4500;
    const remaining = Math.max(0, freeShipping - cart.total_price);
    const pct = Math.min(100, (cart.total_price / freeShipping) * 100);

    const shippingBar = remaining > 0
      ? `<div style="padding:var(--space-3);background:var(--color-secondary);border-radius:var(--radius-md);margin-bottom:var(--space-3);">
           <p style="font-size:var(--text-xs);color:var(--color-text-muted);margin-bottom:var(--space-2);">
             Te faltan <strong>${VYNA.formatMoney(remaining)}</strong> para <strong>envío gratis</strong>
           </p>
           <div style="height:4px;background:var(--color-border);border-radius:999px;overflow:hidden;">
             <div style="height:100%;width:${pct}%;background:var(--color-primary);border-radius:999px;transition:width .3s;"></div>
           </div>
         </div>`
      : `<div style="background:rgba(45,106,79,0.1);border-radius:var(--radius-md);padding:var(--space-3);margin-bottom:var(--space-3);text-align:center;">
           <p style="font-size:var(--text-sm);color:var(--color-success);font-weight:600;">✓ Envío gratis aplicado</p>
         </div>`;

    const items = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img src="${item.image}" alt="${item.title}" class="cart-item__image" loading="lazy" width="80" height="80">
        <div class="cart-item__details">
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <div class="cart-item__qty">
            <button class="cart-item__qty-decrease" data-key="${item.key}" aria-label="Reducir">−</button>
            <span>${item.quantity}</span>
            <button class="cart-item__qty-increase" data-key="${item.key}" aria-label="Aumentar">+</button>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Eliminar" style="margin-left:auto;font-size:11px;color:var(--color-text-muted);text-decoration:underline;cursor:pointer;">Eliminar</button>
          </div>
        </div>
        <p class="cart-item__price">${VYNA.formatMoney(item.final_line_price)}</p>
      </div>`).join('');

    const footer = `
      <div class="cart-drawer__footer">
        ${shippingBar}
        <div class="cart-drawer__subtotal">
          <span>Subtotal</span>
          <strong>${VYNA.formatMoney(cart.total_price)}</strong>
        </div>
        <p style="font-size:var(--text-xs);color:var(--color-text-muted);margin-bottom:var(--space-4);">
          Impuestos y envío calculados en el checkout
        </p>
        <a href="/checkout" class="btn btn--primary btn--full btn--lg">Finalizar compra</a>
        <button class="btn btn--secondary btn--full" style="margin-top:var(--space-2);" onclick="window.vynaCart?.close()">
          Seguir comprando
        </button>
      </div>`;

    this.itemsEl.innerHTML = items;
    const existingFooter = this.drawer.querySelector('.cart-drawer__footer');
    if (existingFooter) existingFooter.remove();
    this.drawer.insertAdjacentHTML('beforeend', footer);
  }

  updateCount(count) {
    const countEl = document.querySelector('.header-cart-count');
    const totalEl = document.querySelector('.header-cart-total');
    if (countEl) {
      countEl.textContent = count;
      countEl.style.display = count > 0 ? 'inline-flex' : 'none';
    }
  }

  updateTotal(price) {
    const totalEl = document.querySelector('.header-cart-total');
    if (totalEl) totalEl.textContent = VYNA.formatMoney(price);
  }

  trackAddToCart(variantId, qty) {
    // Meta Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'AddToCart', {
        content_ids: [variantId.toString()],
        content_type: 'product',
        currency: 'EUR'
      });
    }
    // GA4 vía dataLayer
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'EUR',
        items: [{ item_id: variantId.toString(), quantity: qty }]
      }
    });
  }
}

/* =========================================
   VARIANTES DE PRODUCTO (PDP)
   ========================================= */
class VynaProductPage {
  constructor() {
    this.product = null;
    this.selectedOptions = {};
    this.purchaseType = 'onetime';

    if (!document.querySelector('.pdp')) return;
    this.init();
  }

  async init() {
    const handle = document.querySelector('[data-product-handle]')?.dataset.productHandle;
    if (!handle) return;

    try {
      const data = await VYNA.fetchJSON(`/products/${handle}.js`);
      this.product = data;
      this.bindVariantSelectors();
      this.bindPurchaseType();
      this.bindGallery();
      this.trackViewItem(data);
    } catch (err) {
      console.error('Error cargando producto:', err);
    }
  }

  bindVariantSelectors() {
    document.querySelectorAll('.pdp__size-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const position = parseInt(btn.dataset.optionPosition);
        const value    = btn.dataset.value;

        // Update active state
        document.querySelectorAll(`.pdp__size-btn[data-option-position="${position}"]`).forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');

        // Update displayed value
        const displayEl = document.getElementById(`selected-${position}`);
        if (displayEl) displayEl.textContent = value;

        this.selectedOptions[position] = value;
        this.updateVariant();
      });
    });
  }

  updateVariant() {
    if (!this.product) return;
    const variant = this.product.variants.find(v => {
      return Object.entries(this.selectedOptions).every(([pos, val]) => {
        const optIdx = parseInt(pos) - 1;
        return v[`option${pos}`] === val;
      });
    });

    if (!variant) return;

    // Update hidden input
    const input = document.getElementById('selected-variant-id');
    if (input) input.value = variant.id;

    // Update prices
    const priceOneTime = document.getElementById('price-onetime');
    const priceSub     = document.getElementById('price-subscription');
    if (priceOneTime) priceOneTime.textContent = VYNA.formatMoney(variant.price);
    if (priceSub)     priceSub.textContent     = VYNA.formatMoney(Math.round(variant.price * 0.80));

    // Update ATC button
    const atcBtn  = document.getElementById('add-to-cart-btn');
    const buyNow  = document.getElementById('buy-now-btn');
    if (atcBtn) {
      atcBtn.dataset.variantId = variant.id;
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? 'Añadir al carrito' : 'Agotado';
    }
    if (buyNow) buyNow.dataset.variantId = variant.id;

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  bindPurchaseType() {
    const oneTime         = document.getElementById('purchase-onetime');
    const sub             = document.getElementById('purchase-subscription');
    const freqSel         = document.getElementById('frequency-selector');
    const freqSelect      = document.getElementById('delivery-frequency');
    const sellingPlanInput = document.getElementById('selected-selling-plan');

    // Build week-count → selling_plan_id map from Shopify product data
    const planMap = {};
    if (this.product?.selling_plan_groups) {
      this.product.selling_plan_groups.forEach(group => {
        (group.selling_plans || []).forEach(plan => {
          const policy = plan.billing_policy || plan.delivery_policy;
          if (!policy) return;
          const interval = (policy.interval || '').toUpperCase();
          const count    = policy.interval_count;
          if (interval === 'WEEK')  planMap[count]      = plan.id;
          if (interval === 'MONTH') planMap[count * 4]  = plan.id; // 1 month ≈ 4 weeks
        });
      });
    }

    // Returns the best matching plan ID for the currently selected frequency
    const getActivePlanId = () => {
      const weeks = parseInt(freqSelect?.value) || 4;
      if (planMap[weeks]) return planMap[weeks];
      // Fallback: first available plan
      const allIds = Object.values(planMap);
      return allIds.length ? allIds[0] : '';
    };

    const updateSellingPlan = () => {
      if (sellingPlanInput) {
        sellingPlanInput.value = this.purchaseType === 'subscription' ? getActivePlanId() : '';
      }
    };

    const selectType = (type) => {
      this.purchaseType = type;
      oneTime?.classList.toggle('is-active', type === 'onetime');
      sub?.classList.toggle('is-active', type === 'subscription');
      oneTime?.setAttribute('aria-checked', type === 'onetime');
      sub?.setAttribute('aria-checked', type === 'subscription');
      if (freqSel) freqSel.style.display = type === 'subscription' ? 'flex' : 'none';
      updateSellingPlan();
    };

    oneTime?.addEventListener('click', () => selectType('onetime'));
    sub?.addEventListener('click',     () => selectType('subscription'));
    freqSelect?.addEventListener('change', updateSellingPlan);

    // Keyboard
    [oneTime, sub].forEach(el => {
      el?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          el.click();
        }
      });
    });
  }

  bindGallery() {
    const mainImg = document.getElementById('gallery-main-image');
    document.querySelectorAll('.pdp__gallery-thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        document.querySelectorAll('.pdp__gallery-thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (mainImg && thumb.dataset.src) {
          mainImg.src = thumb.dataset.src;
          mainImg.alt = thumb.dataset.alt || '';
        }
      });
    });
  }

  trackViewItem(product) {
    const variant = product.variants[0];
    if (!variant) return;

    if (typeof fbq !== 'undefined') {
      fbq('track', 'ViewContent', {
        content_ids:  [variant.id.toString()],
        content_type: 'product',
        value:         variant.price / 100,
        currency:      'EUR'
      });
    }

    window.dataLayer = window.dataLayer || [];
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: 'EUR',
        value:     variant.price / 100,
        items: [{
          item_id:       variant.id.toString(),
          item_name:     product.title,
          item_category: product.type,
          price:         variant.price / 100,
          quantity:      1
        }]
      }
    });
  }
}

/* =========================================
   QUIZ
   ========================================= */
class VynaQuiz {
  constructor(container) {
    this.container = container;
    this.currentStep = 0;
    this.answers = {};

    this.steps = [
      {
        id: 'dog_size',
        question: '¿De qué tamaño es tu perro?',
        options: [
          { value: 'small',  label: 'Pequeño',  sublabel: 'Menos de 10 kg',  icon: '🐕' },
          { value: 'medium', label: 'Mediano',  sublabel: '10 – 25 kg',      icon: '🐕‍🦺' },
          { value: 'large',  label: 'Grande',   sublabel: 'Más de 25 kg',    icon: '🦮' }
        ]
      },
      {
        id: 'dog_age',
        question: '¿Cuántos años tiene?',
        options: [
          { value: 'puppy',  label: 'Cachorro', sublabel: 'Menos de 1 año' },
          { value: 'adult',  label: 'Adulto',   sublabel: '1 – 7 años' },
          { value: 'senior', label: 'Senior',   sublabel: 'Más de 7 años' }
        ]
      },
      {
        id: 'main_concern',
        question: '¿Cuál es tu principal preocupación?',
        options: [
          { value: 'digestion',      label: 'Digestión sensible',  sublabel: 'Vómitos, heces blandas' },
          { value: 'skin_allergies', label: 'Piel y alergias',     sublabel: 'Picor, pelaje opaco' },
          { value: 'weight',         label: 'Peso y energía',      sublabel: 'Control calórico, vitalidad' },
          { value: 'general',        label: 'Nutrición completa',  sublabel: 'Lo mejor para mi perro' }
        ]
      },
      {
        id: 'priority',
        question: '¿Qué valoras más en un pienso?',
        options: [
          { value: 'science',        label: 'Ciencia',            sublabel: 'Ingredientes estudiados' },
          { value: 'sustainability', label: 'Sostenibilidad',     sublabel: 'Menor impacto ambiental' },
          { value: 'quality',        label: 'Calidad',            sublabel: 'Fuentes premium' },
          { value: 'value',          label: 'Precio/valor',       sublabel: 'La mejor relación calidad/precio' }
        ]
      }
    ];

    this.recommendations = {
      small:  { puppy: 'vyna-adult-small', adult: 'vyna-adult-small',    senior: 'vyna-adult-small' },
      medium: { puppy: 'vyna-adult-all-breed', adult: 'vyna-adult-all-breed', senior: 'vyna-adult-all-breed' },
      large:  { puppy: 'vyna-adult-large', adult: 'vyna-adult-large',   senior: 'vyna-adult-large' }
    };

    this.render();
    this.bindEvents();
  }

  render() {
    if (this.currentStep < this.steps.length) {
      this.renderStep(this.steps[this.currentStep]);
    } else {
      this.renderResult();
    }
  }

  renderStep(step) {
    const pct = (this.currentStep / this.steps.length) * 100;
    const gridClass = step.options.length === 3 ? 'quiz__options--3' : 'quiz__options--4';

    this.container.innerHTML = `
      <div class="quiz__progress">
        <div class="quiz__progress-bar" style="width:${pct}%"></div>
        <span class="quiz__step-counter">${this.currentStep + 1} / ${this.steps.length}</span>
      </div>
      <h2 class="quiz__question">${step.question}</h2>
      <div class="quiz__options ${gridClass}" role="group" aria-label="${step.question}">
        ${step.options.map(opt => `
          <button
            class="quiz__option"
            data-value="${opt.value}"
            aria-label="${opt.label}: ${opt.sublabel || ''}">
            ${opt.icon ? `<span class="quiz__option-icon" aria-hidden="true">${opt.icon}</span>` : ''}
            <span class="quiz__option-label">${opt.label}</span>
            ${opt.sublabel ? `<span class="quiz__option-sublabel">${opt.sublabel}</span>` : ''}
          </button>`).join('')}
      </div>`;
  }

  async renderResult() {
    const handle = this.getRecommendedHandle();

    this.container.innerHTML = `
      <div class="quiz__result">
        <p class="quiz__result-eyebrow">Tu recomendación personalizada</p>
        <h2 class="quiz__result-headline">Hemos encontrado el VYNA ideal para tu perro</h2>
        <div id="quiz-product-card" style="margin:var(--space-8) 0;min-height:200px;display:flex;align-items:center;justify-content:center;">
          <div style="color:var(--color-text-muted);">Cargando producto recomendado...</div>
        </div>
        <div style="background:var(--color-secondary);border-radius:var(--radius-lg);padding:var(--space-6);margin-bottom:var(--space-6);">
          <p style="font-weight:600;margin-bottom:var(--space-3);">
            Guarda tu recomendación y recibe un <span style="color:var(--color-gold);">10% de descuento</span>
          </p>
          <form class="quiz__email-form" id="quiz-email-form">
            <input
              type="email"
              placeholder="tu@email.com"
              required
              aria-label="Tu email"
              style="flex:1;padding:var(--space-3) var(--space-4);border:1px solid var(--color-border);border-radius:var(--radius-md);font-size:var(--text-sm);">
            <button type="submit" class="btn btn--primary">Obtener descuento</button>
          </form>
        </div>
        <a href="/products/${handle}" class="quiz__result-skip" style="font-size:var(--text-sm);color:var(--color-text-muted);display:block;text-align:center;">
          Ver producto sin descuento →
        </a>
      </div>`;

    this.fetchAndRenderProduct(handle);
    this.bindEmailForm(handle);
    this.trackQuizComplete(handle);
  }

  async fetchAndRenderProduct(handle) {
    try {
      const product = await VYNA.fetchJSON(`/products/${handle}.js`);
      const cardEl  = document.getElementById('quiz-product-card');
      if (!cardEl) return;

      const variant = product.variants[0];
      const img     = product.featured_image || '';

      cardEl.innerHTML = `
        <div style="display:grid;grid-template-columns:140px 1fr;gap:var(--space-5);align-items:center;width:100%;border:1px solid var(--color-border-light);border-radius:var(--radius-lg);padding:var(--space-4);">
          ${img ? `<img src="${img}" alt="${product.title}" style="width:140px;height:140px;object-fit:cover;border-radius:var(--radius-md);">` : ''}
          <div>
            <p style="font-family:var(--font-data);font-size:var(--text-xs);font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:var(--color-gold);margin-bottom:var(--space-1);">Recomendado para tu perro</p>
            <h3 style="font-family:var(--font-display);font-size:var(--text-xl);margin-bottom:var(--space-2);">${product.title}</h3>
            <p style="font-family:var(--font-data);font-size:var(--text-2xl);font-weight:700;color:var(--color-primary);margin-bottom:var(--space-4);">
              ${VYNA.formatMoney(variant.price)}
            </p>
            <a href="/products/${handle}" class="btn btn--primary btn--pill">Ver producto →</a>
          </div>
        </div>`;
    } catch (err) {
      console.error('Quiz product fetch failed:', err);
    }
  }

  bindEmailForm(handle) {
    const form = document.getElementById('quiz-email-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = form.querySelector('input[type="email"]').value;
      const btn   = form.querySelector('button');

      btn.disabled = true;
      btn.textContent = 'Procesando...';

      // Klaviyo identify
      if (typeof klaviyo !== 'undefined') {
        try {
          await klaviyo.identify({ email, quiz_result: handle });
        } catch(err) { /* silent */ }
      }

      // Redirección con descuento
      btn.textContent = '¡Descuento aplicado!';
      setTimeout(() => {
        window.location.href = `/products/${handle}?discount=QUIZ10`;
      }, 800);
    });
  }

  getRecommendedHandle() {
    const size = this.answers.dog_size || 'medium';
    const age  = this.answers.dog_age  || 'adult';
    return this.recommendations[size]?.[age] || 'vyna-adult-all-breed';
  }

  trackQuizComplete(handle) {
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'QuizCompleted', { recommended_sku: handle });
    }
    window.dataLayer = window.dataLayer || [];
    dataLayer.push({ event: 'quiz_complete', recommended_sku: handle });
    VYNA.emit('quiz_complete', { handle });
  }

  bindEvents() {
    this.container.addEventListener('click', (e) => {
      const opt = e.target.closest('.quiz__option');
      if (!opt) return;

      const step = this.steps[this.currentStep];
      this.answers[step.id] = opt.dataset.value;

      opt.classList.add('is-selected');
      opt.setAttribute('aria-pressed', 'true');

      setTimeout(() => {
        this.currentStep++;
        this.render();
      }, 380);
    });
  }
}

/* =========================================
   MOBILE MENU
   ========================================= */
class VynaMobileMenu {
  constructor() {
    this.toggle = document.getElementById('mobile-menu-toggle');
    this.nav    = document.getElementById('mobile-nav');
    this.isOpen = false;

    if (!this.toggle || !this.nav) return;
    this.bindEvents();
  }

  bindEvents() {
    this.toggle.addEventListener('click', () => this.isOpen ? this.close() : this.open());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.toggle.contains(e.target) && !this.nav.contains(e.target)) {
        this.close();
      }
    });
  }

  open() {
    this.isOpen = true;
    this.nav.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.toggle.setAttribute('aria-label', 'Cerrar menú');
  }

  close() {
    this.isOpen = false;
    this.nav.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-label', 'Abrir menú');
  }
}

/* =========================================
   COOKIE CONSENT (Consent Mode v2)
   ========================================= */
class VynaCookieConsent {
  constructor() {
    this.banner      = document.getElementById('cookie-consent');
    this.acceptBtn   = document.getElementById('cookie-accept-all');
    this.rejectBtn   = document.getElementById('cookie-reject');
    this.cookieName  = 'vyna_consent';

    if (!this.banner) return;
    this.init();
  }

  init() {
    const saved = VYNA.getCookie(this.cookieName);
    if (!saved) {
      setTimeout(() => {
        this.banner.style.display = 'block';
      }, 1500);
    } else {
      this.applyConsent(saved === 'granted');
    }

    this.acceptBtn?.addEventListener('click', () => {
      this.saveConsent(true);
      this.hideBanner();
    });

    this.rejectBtn?.addEventListener('click', () => {
      this.saveConsent(false);
      this.hideBanner();
    });
  }

  saveConsent(granted) {
    VYNA.setCookie(this.cookieName, granted ? 'granted' : 'denied', 365);
    this.applyConsent(granted);
  }

  applyConsent(granted) {
    const state = granted ? 'granted' : 'denied';
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        ad_storage:        state,
        analytics_storage: state,
        ad_user_data:      state,
        ad_personalization: state
      });
    }
  }

  hideBanner() {
    this.banner.style.opacity = '0';
    setTimeout(() => { this.banner.style.display = 'none'; }, 400);
  }
}

/* =========================================
   NEWSLETTER FORM
   ========================================= */
class VynaNewsletter {
  constructor() {
    document.querySelectorAll('[id^="footer-newsletter"]').forEach(form => {
      form.addEventListener('submit', (e) => {
        const emailInput = form.querySelector('input[type="email"]');
        if (!emailInput?.value) return;
        // Track
        if (typeof fbq !== 'undefined') {
          fbq('track', 'Lead', { content_name: 'newsletter_footer' });
        }
        dataLayer.push({ event: 'newsletter_signup', source: 'footer' });
        VYNA.emit('newsletter_signup', { email: emailInput.value, source: 'footer' });
      });
    });
  }
}

/* =========================================
   NOTIFICATIONS
   ========================================= */
class VynaNotification {
  static show(message, type = 'success', duration = 3000) {
    const colors = {
      success: 'var(--color-success)',
      error:   'var(--color-error)',
      info:    'var(--color-primary)'
    };

    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    Object.assign(el.style, {
      position:    'fixed',
      top:         '80px',
      right:       '20px',
      background:  colors[type] || colors.info,
      color:       '#fff',
      padding:     '12px 20px',
      borderRadius:'8px',
      fontSize:    '14px',
      fontWeight:  '500',
      zIndex:      '9999',
      boxShadow:   '0 4px 16px rgba(0,0,0,0.15)',
      transform:   'translateX(120%)',
      transition:  'transform 0.3s ease',
      maxWidth:    '320px'
    });
    el.textContent = message;
    document.body.appendChild(el);

    requestAnimationFrame(() => {
      el.style.transform = 'translateX(0)';
    });

    setTimeout(() => {
      el.style.transform = 'translateX(120%)';
      setTimeout(() => el.remove(), 300);
    }, duration);
  }
}

/* =========================================
   COOKIE BANNER CSS
   ========================================= */
const cookieStyles = document.createElement('style');
cookieStyles.textContent = `
  .cookie-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--color-primary);
    color: rgba(255,255,255,0.9);
    z-index: 9000;
    padding: var(--space-4) 0;
    box-shadow: 0 -4px 20px rgba(0,0,0,0.15);
    transition: opacity .4s ease;
  }
  .cookie-banner__inner {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .cookie-banner__text {
    font-size: var(--text-sm);
    flex: 1;
    min-width: 200px;
  }
  .cookie-banner__text a {
    color: var(--color-gold);
    text-decoration: underline;
  }
  .cookie-banner__actions {
    display: flex;
    gap: var(--space-3);
    flex-shrink: 0;
  }
`;
document.head.appendChild(cookieStyles);

/* =========================================
   INICIALIZACIÓN
   ========================================= */
document.addEventListener('DOMContentLoaded', () => {
  // Carrito
  window.vynaCart = new VynaCart();

  // Página de producto
  new VynaProductPage();

  // Quiz
  const quizContainer = document.getElementById('vyna-quiz');
  if (quizContainer) new VynaQuiz(quizContainer);

  // Mobile menu
  new VynaMobileMenu();

  // Cookie consent
  new VynaCookieConsent();

  // Newsletter
  new VynaNewsletter();

  // Notificaciones de éxito post-purchase (URL param)
  const params = new URLSearchParams(window.location.search);
  if (params.get('order_confirmed')) {
    VynaNotification.show('¡Pedido confirmado! Recibirás un email con los detalles.', 'success', 5000);
  }
});
